import { useMutation, useQueries, useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { BackChevron } from '../components/BackChevron';
import {
  ApiError,
  createOrder,
  getTicketTypes,
  listProductComponents,
  listSchedules,
  type CreateOrderItem,
  type CreateOrderRequest,
  type PackageComponent,
  type ProductSummary,
  type ScheduleSummary,
  type SellableTicketType,
} from '../lib/api-client';
import { formatDateTime, formatPrice, pickI18n } from '../lib/format';
import { useI18n } from '../lib/i18n-context';
import { ticketLabel } from '../lib/ticket-label';

/** 班次可售：未天候取消且尚有座位。 */
function isPurchasable(s: ScheduleSummary): boolean {
  return !s.weather_cancelled && s.capacity - s.reserved_count > 0;
}

function overlaps(a: ScheduleSummary, b: ScheduleSummary): boolean {
  return (
    new Date(a.departure_at) < new Date(b.departure_end_at) &&
    new Date(b.departure_at) < new Date(a.departure_end_at)
  );
}

function newIdempotencyKey(): string {
  return globalThis.crypto?.randomUUID?.() ?? `idem-${Date.now()}-${Math.random().toString(16)}`;
}

/**
 * 套票購買 — 逐段選時段。
 *
 * 套票商品不建班次，它只定義「含哪些商品」，座位扣在各組成商品的真實班次上。
 * 此前 PWA 對套票只會走單一班次的購買頁，查 `/schedules?product_id=<套票>` 得到
 * 0 筆 —— 商品列得出來、點進去卻是一片空白，買不了。
 *
 * ## 與 `/pricing/quote` 的差別
 *
 * 套票沒有 `schedule_id`，而 quote 那支要它，所以這裡不呼叫 quote：單價直接取
 * `GET /pricing/ticket-types`（不帶 schedule_id）的商品價目表，與後端下單時分攤
 * 打包價所用的是同一組數字，畫面金額與落庫值一致。
 */
export function PackageBuyPage({ product }: { product: ProductSummary }): JSX.Element {
  const navigate = useNavigate();
  const { t, locale } = useI18n();

  /** 組成序號 → 選定的 schedule_id。 */
  const [picked, setPicked] = useState<Record<number, string>>({});
  const [counts, setCounts] = useState<Record<string, number>>({});

  const componentsQuery = useQuery({
    queryKey: ['product-components', product.id],
    queryFn: () => listProductComponents(product.id),
    staleTime: 60_000,
  });
  const components: PackageComponent[] = useMemo(
    () => componentsQuery.data ?? [],
    [componentsQuery.data],
  );

  const scheduleQueries = useQueries({
    queries: components.map((c) => ({
      queryKey: ['schedules', c.component_product_id],
      queryFn: () => listSchedules(c.component_product_id),
      staleTime: 30_000,
    })),
  });

  const chosen: Array<ScheduleSummary | null> = components.map((_, i) => {
    const id = picked[i];
    if (id === undefined) return null;
    return scheduleQueries[i]?.data?.items.find((s) => s.id === id) ?? null;
  });
  const allPicked = components.length > 0 && chosen.every((s) => s !== null);

  // 套票票種不帶 schedule_id（它沒有班次）
  const ticketTypesQuery = useQuery({
    queryKey: ['ticket-types', 'package', product.id],
    queryFn: () => getTicketTypes({ product_id: product.id, channel: 'web' }),
    staleTime: 30_000,
  });
  const ticketTypes: SellableTicketType[] = ticketTypesQuery.data?.ticket_types ?? [];

  const qtyOf = (tt: string): number => counts[tt] ?? 0;
  const partySize = ticketTypes.reduce((sum, tt) => sum + qtyOf(tt.ticket_type), 0);

  /* 可買人數上限 = 各段剩餘座位的最小值；任何一段滿了整趟就走不完。 */
  const seatCap = allPicked
    ? Math.min(
        ...chosen.map(
          (s) => (s as ScheduleSummary).capacity - (s as ScheduleSummary).reserved_count,
        ),
      )
    : 0;

  const subtotal = ticketTypes.reduce(
    (sum, tt) => sum + Number(tt.unit_price) * qtyOf(tt.ticket_type),
    0,
  );
  const canSubmit = allPicked && partySize >= 1 && partySize <= seatCap;

  const createOrderMutation = useMutation({
    mutationFn: async () => {
      const items: CreateOrderItem[] = [];
      components.forEach((c, i) => {
        const schedule = chosen[i];
        if (schedule === null || schedule === undefined) return;
        for (const tt of ticketTypes) {
          const qty = qtyOf(tt.ticket_type);
          if (qty <= 0) continue;
          items.push({
            product_id: c.component_product_id,
            schedule_id: schedule.id,
            quantity: qty,
            // 後端會以打包價分攤覆寫；此處僅供 DTO 結構完整
            unit_price: 0,
            ticket_type: tt.ticket_type,
            package_product_id: product.id,
          });
        }
      });
      const payload: CreateOrderRequest = {
        channel: 'web',
        subtotal,
        total: subtotal,
        items,
      };
      return createOrder(payload, newIdempotencyKey());
    },
    onSuccess: (order) => navigate(`/orders/${order.id}/checkout`),
  });

  const setCount = (tt: string, n: number): void =>
    setCounts((c) => ({ ...c, [tt]: Math.max(0, Math.min(seatCap || 0, n)) }));

  return (
    <div className="app-shell">
      <header className="topbar topbar-lead">
        <BackChevron to={`/products/${product.id}`} label={t('common.back')} />
        <h1>{t('buy.title')}</h1>
      </header>

      <p style={{ color: '#555', margin: '4px 0 4px' }}>
        {pickI18n(product.name_i18n, product.sku, locale)}
      </p>
      <p style={{ color: '#777', fontSize: 13, margin: '0 0 12px' }}>{t('buy.pkgLead')}</p>

      {componentsQuery.isPending && <p>{t('buy.loadingSchedules')}</p>}
      {componentsQuery.isSuccess && components.length === 0 && (
        <p className="error-text">{t('buy.pkgNotSellable')}</p>
      )}

      {components.map((c, i) => {
        const query = scheduleQueries[i];
        const items = (query?.data?.items ?? []).filter(isPurchasable);
        const others = chosen.filter((s, idx): s is ScheduleSummary => idx !== i && s !== null);
        return (
          <section key={c.component_product_id} style={{ marginBottom: 18 }}>
            <h2 style={{ fontSize: 15, margin: '0 0 2px' }}>
              {t('buy.pkgSegment', { n: String(i + 1) })}｜{pickI18n(c.name_i18n, c.sku, locale)}
            </h2>
            {c.duration_minutes !== null && (
              <p style={{ fontSize: 13, color: '#777', margin: '0 0 8px' }}>
                {c.duration_minutes} {t('buy.minutes')}
              </p>
            )}
            {query?.isPending === true && <p>{t('buy.loadingSchedules')}</p>}
            {query?.isSuccess === true && items.length === 0 && <p>{t('buy.pkgNoSlot')}</p>}
            {items.map((s) => {
              const clash = others.some((o) => overlaps(o, s));
              const selected = picked[i] === s.id;
              const disabled = clash && !selected;
              return (
                <label
                  key={s.id}
                  className="card"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    opacity: disabled ? 0.55 : 1,
                    border: selected ? '2px solid #0E5A8C' : '1px solid transparent',
                  }}
                >
                  <input
                    type="radio"
                    name={`seg-${i}`}
                    value={s.id}
                    checked={selected}
                    disabled={disabled}
                    onChange={() => setPicked((p) => ({ ...p, [i]: s.id }))}
                    style={{ accentColor: '#0e5a8c', width: 18, height: 18 }}
                  />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 500 }}>{formatDateTime(s.departure_at, locale)}</div>
                    <div style={{ fontSize: 13, color: '#777' }}>
                      {t('buy.until', { time: formatDateTime(s.departure_end_at, locale) })}
                    </div>
                    <div style={{ fontSize: 13, color: disabled ? '#b45309' : '#555' }}>
                      {disabled
                        ? t('buy.pkgClash')
                        : t('buy.remaining', {
                            remaining: String(s.capacity - s.reserved_count),
                            capacity: String(s.capacity),
                          })}
                    </div>
                  </div>
                </label>
              );
            })}
          </section>
        );
      })}

      {!allPicked && components.length > 0 && (
        <p style={{ color: '#777', fontSize: 13 }}>{t('buy.pkgPickEach')}</p>
      )}

      {allPicked && (
        <section>
          <p style={{ color: '#777', fontSize: 13 }}>{t('buy.pkgLimit', { n: String(seatCap) })}</p>
          {ticketTypes.map((tt) => (
            <div
              key={tt.ticket_type}
              style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}
            >
              <span style={{ flex: 1 }}>
                {ticketLabel(tt, locale)}
                {t('buy.unitPrice', { price: formatPrice(tt.unit_price, product.currency) })}
              </span>
              <input
                type="number"
                min={0}
                max={seatCap}
                value={qtyOf(tt.ticket_type)}
                aria-label={t('buy.quantity')}
                onChange={(e) => setCount(tt.ticket_type, Number(e.target.value))}
                style={{ width: 72 }}
              />
            </div>
          ))}

          <p style={{ fontWeight: 600, marginTop: 12 }}>
            {t('buy.subtotal')}
            {formatPrice(String(subtotal), product.currency)}
          </p>

          {createOrderMutation.isError && (
            <p className="error-text">
              {t('buy.orderFailed')}
              {createOrderMutation.error instanceof ApiError
                ? (createOrderMutation.error.problem?.detail ?? createOrderMutation.error.message)
                : t('common.unknownError')}
            </p>
          )}

          <button
            type="button"
            className="primary"
            disabled={!canSubmit || createOrderMutation.isPending}
            onClick={() => createOrderMutation.mutate()}
          >
            {createOrderMutation.isPending ? t('buy.submitting') : t('buy.submit')}
          </button>
        </section>
      )}
    </div>
  );
}
